<div [class]="containerClass()">
  @if (showHeader()) {
    <div class="flex items-center justify-between mb-6">
      <div class="flex items-center gap-4">
        <h1 class="text-2xl font-bold text-gray-800 m-0">現場ダッシュボード</h1>
      </div>
      
      <!-- Weather News Button -->
      <button *appFeatureToggle="'weather_news'" matStrokedButton color="primary" [matMenuTriggerFor]="wnMenu" class="rounded-full! border-gray-200 shadow-sm hover:bg-gray-50 transition-colors">
        <div class="flex items-center gap-2">
          <img src="assets/images/weather-news.png" alt="Weather News" class="w-6 h-6 object-contain">
          <span class="font-medium">ウェザーニュース</span>
          <mat-icon class="text-gray-500 icon-sm">arrow_drop_down</mat-icon>
        </div>
      </button>
      
      <mat-menu #wnMenu="matMenu">
        <button mat-menu-item (click)="openWeatherNews('today')">
          <mat-icon class="text-orange-500">today</mat-icon>
          <span>今日の天気</span>
        </button>
        <button mat-menu-item (click)="openWeatherNews('tomorrow')">
          <mat-icon class="text-blue-500">event</mat-icon>
          <span>明日の天気</span>
        </button>
        <button mat-menu-item (click)="openWeatherNews('weekly')">
          <mat-icon class="text-green-500">date_range</mat-icon>
          <span>週間天気</span>
        </button>
      </mat-menu>
    </div>
  }

  <app-site-alert-banner
    [latestSiteData]="latestSiteData()"
    [isOuterUser]="isOuterUser()"
  ></app-site-alert-banner>

  @if (siteDetail()) {
    @let detail = siteDetail()!;
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
      <!-- Basic Info Section -->
      <mat-card class="col-span-1 md:col-span-2 shadow-sm rounded-xl border border-gray-100 overflow-hidden">
        <div class="bg-primary-container/20 px-6 py-4 border-b border-gray-100">
          <h2 class="text-lg font-bold text-gray-800 m-0 flex items-center gap-2">
            <mat-icon class="text-primary">domain</mat-icon>
            基本情報
          </h2>
        </div>
        <mat-card-content class="p-6">
          <div class="space-y-4">
            <div>
              <p class="text-sm text-gray-500 mb-1">現場名</p>
              <p class="text-xl font-medium text-gray-900">{{ detail.name }}</p>
            </div>
            <mat-divider></mat-divider>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p class="text-sm text-gray-500 mb-1">住所</p>
                <p class="text-base text-gray-800">{{ detail.address1 }} {{ detail.address2 }}</p>
              </div>
              <div>
                <p class="text-sm text-gray-500 mb-1">管理部署</p>
                <p class="text-base text-gray-800 flex items-center gap-2">
                  <mat-icon class="text-gray-400 text-sm">business</mat-icon>
                  {{ detail.managementDepartmentName }}
                </p>
              </div>
            </div>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Latest Status Section -->
      <mat-card class="col-span-1 shadow-sm rounded-xl border border-gray-100 overflow-hidden">
        <div class="bg-primary-container/20 px-6 py-4 border-b border-gray-100">
          <h2 class="text-lg font-bold text-gray-800 m-0 flex items-center gap-2">
            <mat-icon class="text-primary">sensors</mat-icon>
            最新ステータス
          </h2>
        </div>
        <mat-card-content class="p-6">
          <div class="space-y-6">
            
            <!-- WBGT -->
            @if (detail.wbgtSensors && detail.wbgtSensors.length > 0) {
              @for (sensor of detail.wbgtSensors; track sensor.id ?? $index) {
                @if (!$first) {
                  <mat-divider></mat-divider>
                }
                <div class="flex items-center justify-between">
                  <div>
                    <p class="text-sm text-gray-500 mb-1 flex items-center gap-1">
                      <mat-icon class="text-sm text-orange-500">thermostat</mat-icon>
                      WBGT
                      @if (sensor.positionName) {
                        <span class="text-xs text-gray-400 ml-1">({{ sensor.positionName }})</span>
                      }
                    </p>
                    <p class="text-2xl font-bold text-gray-900">
                      @if (sensor.wbgt !== undefined && sensor.wbgt !== null) {
                        {{ sensor.wbgt | number:'1.1-1' }} <span class="text-base font-normal text-gray-500">℃</span>
                      } @else {
                        <span class="text-gray-400 text-base">データなし</span>
                      }
                    </p>
                    @if (sensor.date) {
                      <p class="text-xs text-gray-400 mt-0.5">更新: {{ sensor.date }}</p>
                    }
                  </div>
                  <div>
                    <span [class]="sensor.badgeClass">
                      {{ sensor.statusLabel || '不明' }}
                    </span>
                  </div>
                </div>
              }
            } @else {
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm text-gray-500 mb-1 flex items-center gap-1">
                    <mat-icon class="text-sm text-orange-500">thermostat</mat-icon>
                    WBGT
                  </p>
                  <p class="text-2xl font-bold text-gray-900">
                    @if (detail.wbgtValue !== undefined && detail.wbgtValue !== null) {
                      {{ detail.wbgtValue | number:'1.1-1' }} <span class="text-base font-normal text-gray-500">℃</span>
                    } @else {
                      <span class="text-gray-400 text-base">データなし</span>
                    }
                  </p>
                </div>
                <div>
                  <span [class]="detail.wbgtBadgeClass">
                    {{ detail.wbgtStatusLabel }}
                  </span>
                </div>
              </div>
            }
            
            <mat-divider></mat-divider>
            
            <!-- Wind -->
            <div class="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p class="text-sm text-gray-500 mb-1 flex items-center gap-1">
                  <mat-icon class="text-sm text-blue-500">air</mat-icon>
                  風速・風向
                </p>
                <p class="text-2xl font-bold text-gray-900">
                  @if (detail.windSpeedValue !== undefined && detail.windSpeedValue !== null) {
                    {{ detail.windSpeedValue | number:'1.1-1' }} <span class="text-base font-normal text-gray-500">m/s</span>
                    <span class="text-sm font-normal text-gray-600 ml-2">({{ detail.windDirection || '-' }})</span>
                  } @else {
                    <span class="text-gray-400 text-base">データなし</span>
                  }
                </p>
                @if (detail.windDate) {
                  <p class="text-xs text-gray-400 mt-0.5">更新: {{ detail.windDate }}</p>
                }
              </div>
              <div class="flex items-center gap-3">
                @if (detail.windDirectionCode !== undefined) {
                  <app-wind-compass
                    [northAngle]="detail.northAngle ?? 0"
                    [windDirectionCode]="detail.windDirectionCode"
                  ></app-wind-compass>
                }
                <span [class]="detail.windSpeedBadgeClass">
                  {{ detail.windSpeedStatusLabel }}
                </span>
              </div>
            </div>

            @if (detail.noiseSensors && detail.noiseSensors.length > 0) {
              <mat-divider></mat-divider>

              <!-- NOISE -->
              @for (sensor of detail.noiseSensors; track sensor.id ?? $index) {
                @if (!$first) {
                  <mat-divider></mat-divider>
                }
                <div class="space-y-2">
                  <div class="flex items-start justify-between gap-3">
                    <div class="min-w-0">
                      <p class="text-sm text-gray-500 mb-1 flex items-center gap-1">
                        <mat-icon class="text-sm text-purple-500">graphic_eq</mat-icon>
                        騒音・振動
                        @if (sensor.positionName) {
                          <span class="text-xs text-gray-400 ml-1">({{ sensor.positionName }})</span>
                        }
                      </p>
                      @if (sensor.date) {
                        <p class="text-xs text-gray-400 mt-0.5">更新: {{ sensor.date }}</p>
                      }
                    </div>
                    <span [class]="sensor.badgeClass">
                      {{ sensor.statusLabel || '不明' }}
                    </span>
                  </div>
                  <div class="space-y-1 text-sm">
                    <div class="flex items-center justify-between gap-3">
                      <span class="text-xs text-gray-500">騒音 Max</span>
                      <span class="font-semibold text-gray-900">
                        @if (sensor.noiseMax != null) {
                          {{ sensor.noiseMax | number:'1.0-1' }} <span class="text-xs font-normal text-gray-500">dB</span>
                        } @else {
                          <span class="text-gray-400">データなし</span>
                        }
                      </span>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <span class="text-xs text-gray-500">騒音 L5</span>
                      <span class="font-semibold text-gray-900">
                        @if (sensor.noiseL5 != null) {
                          {{ sensor.noiseL5 | number:'1.0-1' }} <span class="text-xs font-normal text-gray-500">dB</span>
                        } @else {
                          <span class="text-gray-400">データなし</span>
                        }
                      </span>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <span class="text-xs text-gray-500">振動 Max</span>
                      <span class="font-semibold text-gray-900">
                        @if (sensor.vibrationMax != null) {
                          {{ sensor.vibrationMax | number:'1.0-1' }} <span class="text-xs font-normal text-gray-500">dB</span>
                        } @else {
                          <span class="text-gray-400">データなし</span>
                        }
                      </span>
                    </div>
                    <div class="flex items-center justify-between gap-3">
                      <span class="text-xs text-gray-500">振動 L10</span>
                      <span class="font-semibold text-gray-900">
                        @if (sensor.vibrationL10 != null) {
                          {{ sensor.vibrationL10 | number:'1.0-1' }} <span class="text-xs font-normal text-gray-500">dB</span>
                        } @else {
                          <span class="text-gray-400">データなし</span>
                        }
                      </span>
                    </div>
                  </div>
                </div>
              }
            }

          </div>
        </mat-card-content>
      </mat-card>

      <!-- Chart Controls & Graphs -->
      <mat-card class="col-span-1 md:col-span-3 shadow-sm rounded-xl border border-gray-100 overflow-hidden">
        <div class="bg-primary-container/20 px-6 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-4">
          <h2 class="text-lg font-bold text-gray-800 m-0 flex items-center gap-2">
            <mat-icon class="text-primary">insights</mat-icon>
            センサートレンドグラフ
          </h2>
          
          <div class="flex items-center gap-2">
            <mat-button-toggle-group [value]="seasonMode()" (change)="seasonMode.set($event.value)" aria-label="季節モード">
              <mat-button-toggle value="summer">夏期 (WBGT)</mat-button-toggle>
              <mat-button-toggle value="winter">冬期 (温湿度)</mat-button-toggle>
            </mat-button-toggle-group>
            
            <mat-button-toggle-group [value]="period()" (change)="onPeriodChange($event.value)" aria-label="グラフの期間">
              <mat-button-toggle value="1d">1日</mat-button-toggle>
              <mat-button-toggle value="1w">1週間</mat-button-toggle>
              <mat-button-toggle value="1m">1ヶ月</mat-button-toggle>
            </mat-button-toggle-group>
          </div>
        </div>
        <mat-card-content class="p-6">
          <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            @for (group of wbgtSensorGroups(); track group.sensorId) {
              <section class="bg-white rounded-lg border border-gray-100 p-4 space-y-4">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h3 class="text-base font-bold text-gray-800 m-0 flex items-center gap-2">
                      <mat-icon class="text-orange-500">thermostat</mat-icon>
                      WBGT
                    </h3>
                    <p class="text-sm text-gray-500 mt-1">{{ group.positionName || '未設定' }}</p>
                  </div>
                  @if (group.latest.statusLabel) {
                    <span [class]="group.latest.badgeClass">
                      {{ group.latest.statusLabel }}
                    </span>
                  }
                </div>

                <div data-testid="sensor-latest-value" class="rounded-lg bg-gray-50 px-4 py-3 h-32 flex flex-col justify-between">
                  <p class="text-xs text-gray-500 mb-1">{{ group.latest.label }}</p>
                  <p class="text-2xl font-bold text-gray-900">
                    @if (group.latest.value !== undefined && group.latest.value !== null) {
                      {{ group.latest.value | number:'1.1-1' }} <span class="text-sm font-normal text-gray-500">{{ group.latest.unit }}</span>
                    } @else {
                      <span class="text-base font-normal text-gray-400">データなし</span>
                    }
                  </p>
                  @if (group.latest.date) {
                    <p class="text-xs text-gray-400 mt-1">更新: {{ group.latest.date }}</p>
                  }
                </div>

                <div class="space-y-4">
                  @if (seasonMode() === 'summer') {
                    <div class="rounded-lg border border-gray-100 p-4">
                      <h4 class="text-sm font-semibold text-gray-600 mb-2">WBGT（℃）</h4>
                      <div class="h-48">
                        <app-wbgt-line-chart [seriesData]="group.wbgt" [period]="period()"></app-wbgt-line-chart>
                      </div>
                    </div>
                  }
                  <div class="bg-white rounded-lg border border-gray-100 p-4">
                    <h4 class="text-sm font-semibold text-gray-600 mb-2">温度（℃）</h4>
                    <div class="h-48">
                      <app-temperature-line-chart [seriesData]="group.temperature" [period]="period()"></app-temperature-line-chart>
                    </div>
                  </div>
                  <div class="bg-white rounded-lg border border-gray-100 p-4">
                    <h4 class="text-sm font-semibold text-gray-600 mb-2">湿度（%）</h4>
                    <div class="h-48">
                      <app-humidity-line-chart [seriesData]="group.humidity" [period]="period()"></app-humidity-line-chart>
                    </div>
                  </div>
                </div>
              </section>
            }

            @for (group of windSensorGroups(); track group.sensorId) {
              <section class="bg-white rounded-lg border border-gray-100 p-4 space-y-4">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h3 class="text-base font-bold text-gray-800 m-0 flex items-center gap-2">
                      <mat-icon class="text-blue-500">air</mat-icon>
                      WIND
                    </h3>
                    <p class="text-sm text-gray-500 mt-1">{{ group.positionName || '未設定' }}</p>
                  </div>
                  @if (group.latest.statusLabel) {
                    <span [class]="group.latest.badgeClass">
                      {{ group.latest.statusLabel }}
                    </span>
                  }
                </div>

                <div data-testid="sensor-latest-value" class="rounded-lg bg-gray-50 px-4 py-3 h-32 flex flex-col justify-between">
                  <p class="text-xs text-gray-500 mb-1">{{ group.latest.label }}</p>
                  <div class="flex items-center justify-between gap-3">
                    <p class="text-2xl font-bold text-gray-900">
                      @if (group.latest.value !== undefined && group.latest.value !== null) {
                        {{ group.latest.value | number:'1.1-1' }} <span class="text-sm font-normal text-gray-500">{{ group.latest.unit }}</span>
                        @if (group.windDirection) {
                          <span class="text-sm font-normal text-gray-600 ml-2">({{ group.windDirection }})</span>
                        }
                      } @else {
                        <span class="text-base font-normal text-gray-400">データなし</span>
                      }
                    </p>
                    @if (group.windDirectionCode !== undefined) {
                      <app-wind-compass
                        [northAngle]="group.northAngle"
                        [windDirectionCode]="group.windDirectionCode"
                      ></app-wind-compass>
                    }
                  </div>
                  @if (group.latest.date) {
                    <p class="text-xs text-gray-400 mt-1">更新: {{ group.latest.date }}</p>
                  }
                </div>

                <div class="rounded-lg border border-gray-100 p-4">
                  <h4 class="text-sm font-semibold text-gray-600 mb-2">風速（m/s）</h4>
                  <div class="h-48">
                    <app-wind-line-chart [seriesData]="group.wind" [period]="period()"></app-wind-line-chart>
                  </div>
                </div>
              </section>
            }

            @for (group of noiseSensorGroups(); track group.sensorId) {
              <section class="bg-white rounded-lg border border-gray-100 p-4 space-y-4">
                <div class="flex items-start justify-between gap-3">
                  <div>
                    <h3 class="text-base font-bold text-gray-800 m-0 flex items-center gap-2">
                      <mat-icon class="text-purple-500">graphic_eq</mat-icon>
                      NOISE
                    </h3>
                    <p class="text-sm text-gray-500 mt-1">{{ group.positionName || '未設定' }}</p>
                    @if (group.status?.date) {
                      <p class="text-xs text-gray-400 mt-1">更新: {{ group.status?.date }}</p>
                    }
                  </div>
                  @if (group.status?.statusLabel) {
                    <span [class]="group.status?.badgeClass">
                      {{ group.status?.statusLabel }}
                    </span>
                  }
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div data-testid="sensor-latest-value" class="rounded-lg bg-gray-50 px-4 py-3 h-32 flex flex-col justify-between">
                    <p class="text-xs text-gray-500 mb-1">騒音 L5</p>
                    <p class="text-xl font-bold text-gray-900">
                      @if (group.status?.noiseL5 != null) {
                        {{ group.status?.noiseL5 | number:'1.0-1' }} <span class="text-sm font-normal text-gray-500">dB</span>
                      } @else if (group.latestNoise.value !== undefined && group.latestNoise.value !== null) {
                        {{ group.latestNoise.value | number:'1.0-1' }} <span class="text-sm font-normal text-gray-500">{{ group.latestNoise.unit }}</span>
                      } @else {
                        <span class="text-base font-normal text-gray-400">データなし</span>
                      }
                    </p>
                    <p class="text-xs text-gray-500">Max:
                      @if (group.status?.noiseMax != null) {
                        {{ group.status?.noiseMax | number:'1.0-1' }} dB
                      } @else {
                        -
                      }
                    </p>
                  </div>
                  <div data-testid="sensor-latest-value" class="rounded-lg bg-gray-50 px-4 py-3 h-32 flex flex-col justify-between">
                    <p class="text-xs text-gray-500 mb-1">振動 L10</p>
                    <p class="text-xl font-bold text-gray-900">
                      @if (group.status?.vibrationL10 != null) {
                        {{ group.status?.vibrationL10 | number:'1.0-1' }} <span class="text-sm font-normal text-gray-500">dB</span>
                      } @else if (group.latestVibration.value !== undefined && group.latestVibration.value !== null) {
                        {{ group.latestVibration.value | number:'1.0-1' }} <span class="text-sm font-normal text-gray-500">{{ group.latestVibration.unit }}</span>
                      } @else {
                        <span class="text-base font-normal text-gray-400">データなし</span>
                      }
                    </p>
                    <p class="text-xs text-gray-500">Max:
                      @if (group.status?.vibrationMax != null) {
                        {{ group.status?.vibrationMax | number:'1.0-1' }} dB
                      } @else {
                        -
                      }
                    </p>
                  </div>
                </div>

                <div class="space-y-4">
                  <div class="rounded-lg border border-gray-100 p-4">
                    <h4 class="text-sm font-semibold text-gray-600 mb-2">騒音</h4>
                    <div class="h-48">
                      <app-noise-line-chart [seriesData]="group.noise" [period]="period()"></app-noise-line-chart>
                    </div>
                  </div>
                  <div class="rounded-lg border border-gray-100 p-4">
                    <h4 class="text-sm font-semibold text-gray-600 mb-2">振動</h4>
                    <div class="h-48">
                      <app-vibration-line-chart [seriesData]="group.vibration" [period]="period()"></app-vibration-line-chart>
                    </div>
                  </div>
                </div>
              </section>
            }

            @for (group of phSensorGroups(); track group.sensorId) {
              <section class="bg-white rounded-lg border border-gray-100 p-4 space-y-4">
                <div>
                  <h3 class="text-base font-bold text-gray-800 m-0 flex items-center gap-2">
                    <mat-icon class="text-green-600">science</mat-icon>
                    pH
                  </h3>
                  <p class="text-sm text-gray-500 mt-1">{{ group.positionName || '未設定' }}</p>
                </div>

                <div data-testid="sensor-latest-value" class="rounded-lg bg-gray-50 px-4 py-3 h-32 flex flex-col justify-between">
                  <p class="text-xs text-gray-500 mb-1">{{ group.latest.label }}</p>
                  <p class="text-2xl font-bold text-gray-900">
                    @if (group.latest.value !== undefined && group.latest.value !== null) {
                      {{ group.latest.value | number:'1.1-1' }}
                    } @else {
                      <span class="text-base font-normal text-gray-400">データなし</span>
                    }
                  </p>
                </div>

                <div class="rounded-lg border border-gray-100 p-4">
                  <h4 class="text-sm font-semibold text-gray-600 mb-2">pH</h4>
                  <div class="h-48">
                    <app-ph-line-chart [seriesData]="group.ph" [period]="period()"></app-ph-line-chart>
                  </div>
                </div>
              </section>
            }
          </div>

          @if (
            wbgtSensorGroups().length === 0 &&
            windSensorGroups().length === 0 &&
            noiseSensorGroups().length === 0 &&
            phSensorGroups().length === 0
          ) {
            <div class="flex flex-col items-center justify-center h-48 bg-gray-50 rounded-lg text-gray-400 border border-dashed border-gray-200">
              <mat-icon class="text-4xl mb-2 opacity-50">analytics</mat-icon>
              <p>表示可能なセンサーデータがありません</p>
            </div>
          }
        </mat-card-content>
      </mat-card>
      
    </div>
  } @else {
    <!-- Empty state or loading -->
    <div class="flex flex-col items-center justify-center h-64 bg-white rounded-xl shadow-sm border border-gray-100 text-gray-500">
      <mat-icon class="text-4xl mb-4 text-gray-300">hourglass_empty</mat-icon>
      <p class="text-lg">現場データを読み込んでいます...</p>
      <p class="text-sm text-gray-400 mt-2">データが存在しないか、アクセス権がありません。</p>
    </div>
  }
</div>
